import React, { useState, useEffect, useCallback, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import AntLocalization from '../../utils/AntLocalization';
import AntCondition from '../../utils/AntCondition';
import './index.scss';

var t = function (obj) { return AntLocalization.translateText(obj); };

// ─── Mock Data ───────────────────────────────────────────────────
var MOCK_COLUMNS = [
    { value: 'ToDo', label: { vi: 'Cần làm', en: 'To Do', fr: 'À faire', es: 'Por hacer', id: 'Tertunda', ja: '未着手', zh: '待办', ko: '할 일', hi: 'करना है', ar: 'للتنفيذ' }, color: '#718096' },
    { value: 'InProgress', label: { vi: 'Đang làm', en: 'In Progress', fr: 'En cours', es: 'En curso', id: 'Dikerjakan', ja: '進行中', zh: '进行中', ko: '진행 중', hi: 'प्रगति में', ar: 'قيد التنفيذ' }, color: '#3182CE' },
    { value: 'Review', label: { vi: 'Kiểm tra', en: 'Review', fr: 'Revue', es: 'Revisión', id: 'Tinjauan', ja: 'レビュー', zh: '审核', ko: '검토', hi: 'समीक्षा', ar: 'مراجعة' }, color: '#D69E2E' },
    { value: 'Done', label: { vi: 'Hoàn thành', en: 'Done', fr: 'Terminé', es: 'Completado', id: 'Selesai', ja: '完了', zh: '已完成', ko: '완료', hi: 'पूर्ण', ar: 'مكتمل' }, color: '#38A169' }
];

var MOCK_CARDS = [
    { id: 'TASK-001', code: 'TASK-001', task_status: 'ToDo', name: 'Thiết kế giao diện mới', assignee_name: 'Nguyễn Văn A', priority: 'High', due_date: '2026-03-25', comment_count: 3 },
    { id: 'TASK-002', code: 'TASK-002', task_status: 'ToDo', name: 'Cập nhật API endpoints', assignee_name: 'Trần Thị B', priority: 'Medium', due_date: '2026-03-28', comment_count: 1 },
    { id: 'TASK-003', code: 'TASK-003', task_status: 'InProgress', name: 'Implement search filter', assignee_name: 'Nguyễn Văn A', priority: 'High', due_date: '2026-03-22', comment_count: 5 },
    { id: 'TASK-004', code: 'TASK-004', task_status: 'InProgress', name: 'Fix bug đăng nhập', assignee_name: 'Lê Văn C', priority: 'High', due_date: '2026-03-20', comment_count: 2 },
    { id: 'TASK-005', code: 'TASK-005', task_status: 'Review', name: 'Báo cáo tuần', assignee_name: 'Trần Thị B', priority: 'Low', due_date: '2026-03-26', comment_count: 0 },
    { id: 'TASK-006', code: 'TASK-006', task_status: 'Done', name: 'Setup CI/CD pipeline', assignee_name: 'Lê Văn C', priority: 'Medium', due_date: '2026-03-18', comment_count: 4 },
    { id: 'TASK-007', code: 'TASK-007', task_status: 'Done', name: 'Viết tài liệu hướng dẫn', assignee_name: 'Nguyễn Văn A', priority: 'Low', due_date: '2026-03-19', comment_count: 1 },
    { id: 'TASK-008', code: 'TASK-008', task_status: 'ToDo', name: 'Tối ưu database query', assignee_name: 'Lê Văn C', priority: 'Medium', due_date: '2026-04-01', comment_count: 0 }
];

// ─── Priority colors ─────────────────────────────────────────────
var DEFAULT_PRIORITY_COLORS = {
    High: '#E53E3E',
    Medium: '#D69E2E',
    Low: '#38A169',
    Urgent: '#9B2C2C'
};

// ─── Avatar helpers ──────────────────────────────────────────────
function getInitials(name) {
    if (!name) return '?';
    var parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
}

function getAvatarColor(name) {
    if (!name) return '#9CA3AF';
    var hash = 0;
    for (var i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    var colors = ['#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#06B6D4', '#F97316', '#6366F1'];
    return colors[Math.abs(hash) % colors.length];
}

// ─── Money formatters (theo Sys_BaseCurrency — KHÔNG hardcode) ───
// Cùng khuôn AntHomeMiniGrid.getCurrencyFormatter
function getCurrencyFormatter(compactNotation) {
    try {
        var opts = AntLocalization.formatCurrency(false);
        var lang = opts.language || 'en-US';
        var nfOpts = { signDisplay: opts.signDisplay, maximumFractionDigits: compactNotation ? 1 : 0 };
        if (opts.currency) {
            nfOpts.style = 'currency';
            nfOpts.currency = opts.currency;
            nfOpts.currencyDisplay = 'narrowSymbol';
        }
        if (compactNotation) nfOpts.notation = 'compact';
        var nf = new Intl.NumberFormat(lang, nfOpts);
        // VND hiển thị "đ" đặt sau số như AntLocalization.translateCurrency (Intl trả "₫")
        if (opts.currency === 'VND') return { format: function (n) { return nf.format(n).replace('₫', '').trim() + ' đ'; } };
        return nf;
    } catch (e) {
        return new Intl.NumberFormat('en-US');
    }
}

function formatFieldValue(val, format, compactNotation) {
    if (val == null || val === '') return '';
    var num = parseFloat(val);
    if (isNaN(num)) return String(val);
    try {
        if (format === 'money') return getCurrencyFormatter(compactNotation).format(num);
        return new Intl.NumberFormat((AntLocalization.formatCurrency(false) || {}).language || 'en-US').format(num);
    } catch (e) {
        return String(num);
    }
}

// ANTE trả date/time về dạng Unix timestamp (giây). Chuẩn hoá về Date object:
// - number (giây) → *1000; number quá lớn (ms) giữ nguyên; chuỗi/Date để new Date parse.
function toDate(val) {
    if (val == null || val === '') return null;
    if (val instanceof Date) return val;
    if (typeof val === 'number') return new Date(val < 1e12 ? val * 1000 : val);
    if (/^\d+$/.test(String(val))) {
        var n = Number(val);
        return new Date(n < 1e12 ? n * 1000 : n);
    }
    return new Date(val);
}

function isOverdue(val) {
    var d = toDate(val);
    if (!d || isNaN(d.getTime())) return false;
    var now = new Date();
    return d < now && d.toDateString() !== now.toDateString();
}

function formatDate(val, withTime) {
    var d = toDate(val);
    if (!d || isNaN(d.getTime())) return '';
    var opts = { day: '2-digit', month: '2-digit' };
    if (withTime) { opts.hour = '2-digit'; opts.minute = '2-digit'; }
    return d.toLocaleDateString('vi-VN', opts);
}

// ─── Kanban Card ─────────────────────────────────────────────────
function KanbanCard(props) {
    var { card, cardFields, onCardClick, onDragStart, onDragEnd, dragging, session, cardMenu } = props;
    var [menuOpen, setMenuOpen] = useState(false);

    var titleField = (cardFields && cardFields.title) || 'name';
    var subtitleField = (cardFields && cardFields.subtitle) || 'assignee_name';
    var avatarField = (cardFields && cardFields.avatar) || null;
    var badges = (cardFields && cardFields.badges) || [];
    var footer = (cardFields && cardFields.footer) || [];
    var colorField = (cardFields && cardFields.color) || null;

    var cardTitle = card[titleField] || card.name || card.code || '';
    var cardSubtitle = card[subtitleField] || '';
    var cardAvatar = avatarField ? card[avatarField] : null;
    var accentColor = colorField ? card[colorField] : null;
    var valueSpec = cardFields && cardFields.value; // { field, format: 'money' } — dòng giá trị nổi bật
    var overdue = false;

    // Check visible condition if present
    if (cardFields && cardFields.visible) {
        var vis = AntCondition.checkAll(card, cardFields.visible, session);
        if (!vis) return null;
    }

    return (
        <div
            className={'kanban-card' + (dragging ? ' dragging' : '')}
            style={accentColor ? { borderLeftColor: accentColor } : undefined}
            draggable
            onDragStart={function (e) {
                e.dataTransfer.setData('text/plain', card.id || card.code);
                e.dataTransfer.effectAllowed = 'move';
                if (onDragStart) onDragStart(card);
            }}
            onDragEnd={function () { if (onDragEnd) onDragEnd(); }}
            onClick={function () { if (onCardClick) onCardClick(card); }}
        >
            {/* Menu ⋮ (hover) — hành động nhanh trên card */}
            {cardMenu && cardMenu.items instanceof Array && cardMenu.items.length > 0 && (
                <div className="card-menu-wrap" onClick={function (e) { e.stopPropagation(); }}>
                    <button
                        className="card-menu-btn"
                        onClick={function () { setMenuOpen(!menuOpen); }}
                        onBlur={function () { setTimeout(function () { setMenuOpen(false); }, 150); }}
                    >
                        ⋮
                    </button>
                    {menuOpen && (
                        <div className="card-menu-dropdown">
                            {cardMenu.items.map(function (mi) {
                                if (mi.visible && !mi.visible(card)) return null;
                                return (
                                    <div
                                        key={mi.key}
                                        className="card-menu-item"
                                        onMouseDown={function (e) {
                                            e.preventDefault();
                                            setMenuOpen(false);
                                            if (cardMenu.onSelect) cardMenu.onSelect(mi.key, card);
                                        }}
                                    >
                                        {mi.icon ? <span className="card-menu-icon">{mi.icon}</span> : null}
                                        {typeof mi.label === 'object' ? t(mi.label) : mi.label}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Title */}
            <div className="card-title">{cardTitle}</div>

            {/* Value line (vd giá trị kỳ vọng) */}
            {valueSpec && valueSpec.field && (card[valueSpec.field] || card[valueSpec.field] === 0) && parseFloat(card[valueSpec.field]) !== 0 && (
                <div className="card-value">{formatFieldValue(card[valueSpec.field], valueSpec.format)}</div>
            )}

            {/* Badges */}
            {badges.length > 0 && (
                <div className="card-badges">
                    {badges.map(function (badge, i) {
                        var val = card[badge.field];
                        if (!val && val !== 0) return null;
                        // onlyWhen: chỉ hiện badge khi field = giá trị cụ thể (vd sla_breached=1 → "Quá hạn")
                        if (badge.onlyWhen !== undefined && String(val) !== String(badge.onlyWhen)) return null;

                        if (badge.type === 'date' || badge.type === 'datetime') {
                            var od = isOverdue(val);
                            if (od) overdue = true;
                            return (
                                <span key={i} className={'card-badge date-badge' + (od ? ' overdue' : '')}>
                                    {formatDate(val, badge.type === 'datetime')}
                                </span>
                            );
                        }

                        // labels: map code → nhãn đa ngôn ngữ (tránh hiện mã thô như "High")
                        var display = val;
                        if (badge.labels && badge.labels[val] != null) display = t(badge.labels[val]);
                        else if (typeof val === 'object') display = t(val);

                        // colors: map theo giá trị; color: màu cố định (cho badge text động vd "Im lặng 8 ngày")
                        var badgeColor = (badge.colors && badge.colors[val]) || badge.color;
                        return (
                            <span key={i} className="card-badge" style={badgeColor ? { background: badgeColor + '18', color: badgeColor } : undefined}>
                                {display}
                            </span>
                        );
                    })}
                </div>
            )}

            {/* Footer */}
            <div className="card-footer">
                <div className="card-footer-left">
                    {footer.map(function (f, i) {
                        var val = card[f.field];
                        if (!val && val !== 0) return null;
                        var display = val;
                        var extraClass = '';
                        if (f.type === 'date' || f.type === 'datetime') {
                            display = formatDate(val, f.type === 'datetime');
                            if (isOverdue(val)) extraClass = ' overdue';
                        } else if (f.labels && f.labels[val] != null) {
                            display = t(f.labels[val]);
                        } else if (typeof val === 'object') {
                            display = t(val);
                        }
                        return (
                            <span key={i} className={'card-footer-item' + extraClass}>
                                {f.icon && <i className={'dx-icon-' + f.icon} />}
                                {display}{f.suffix || ''}
                            </span>
                        );
                    })}
                </div>
                <div className="card-footer-right">
                    {cardAvatar
                        ? <img className="card-avatar" src={cardAvatar} alt="" />
                        : cardSubtitle && (
                            <div className="card-avatar-text" style={{ background: getAvatarColor(cardSubtitle) }} title={cardSubtitle}>
                                {getInitials(cardSubtitle)}
                            </div>
                        )
                    }
                </div>
            </div>
        </div>
    );
}

// ─── Kanban Column ───────────────────────────────────────────────
function KanbanColumn(props) {
    var { column, cards, cardFields, statusField, showColumnCount, columnSummary, onCardClick, onDragStart, onDragEnd, onDrop, draggingCard, session, cardMenu } = props;
    var [dragOver, setDragOver] = useState(false);

    var label = typeof column.label === 'object' ? t(column.label) : (column.label || column.value);
    var count = cards.length;
    var wipLimit = column.wipLimit || 0;
    var wipExceeded = wipLimit > 0 && count >= wipLimit;

    // Tổng theo cột (vd doanh thu kỳ vọng) — hiện dạng gọn (compact) để cột hẹp không tràn
    var summaryText = null;
    if (columnSummary && columnSummary.field) {
        var sum = 0;
        for (var ci = 0; ci < cards.length; ci++) sum += parseFloat(cards[ci][columnSummary.field]) || 0;
        if (sum !== 0) summaryText = formatFieldValue(sum, columnSummary.format || 'money', true);
    }

    return (
        <div
            className={'kanban-column' + (dragOver ? ' drag-over' : '') + (wipExceeded ? ' wip-exceeded' : '')}
            onDragOver={function (e) {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                setDragOver(true);
            }}
            onDragLeave={function () { setDragOver(false); }}
            onDrop={function (e) {
                e.preventDefault();
                setDragOver(false);
                var cardId = e.dataTransfer.getData('text/plain');
                if (onDrop) onDrop(cardId, column.value);
            }}
        >
            <div className="column-header">
                <span className="column-color" style={{ background: column.color || '#718096' }} />
                <span className="column-label">{label}</span>
                {summaryText && <span className="column-summary">{summaryText}</span>}
                {showColumnCount !== false && (
                    <span className="column-count">
                        {count}{wipLimit > 0 ? '/' + wipLimit : ''}
                    </span>
                )}
            </div>
            <div className="column-body">
                {cards.map(function (card) {
                    return (
                        <KanbanCard
                            key={card.id || card.code}
                            card={card}
                            cardFields={cardFields}
                            onCardClick={onCardClick}
                            onDragStart={onDragStart}
                            onDragEnd={onDragEnd}
                            session={session}
                            cardMenu={cardMenu}
                        />
                    );
                })}
                {cards.length === 0 && (
                    <div className="column-empty">
                        {t({ vi: 'Trống', en: 'Empty', fr: 'Libre', es: 'Libre', id: 'Kosong', ja: '空き', zh: '空闲', ko: '빈', hi: 'खाली', ar: 'شاغر' })}
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Drop Zone (vùng thả hành động: Thành công / Thất bại / Loại...) ───
function DropZone(props) {
    var { zone, onDropped, draggingCard } = props;
    var [over, setOver] = useState(false);

    var label = typeof zone.label === 'object' ? t(zone.label) : (zone.label || zone.key);
    var color = zone.color || '#6B7280';

    return (
        <div
            className={'kanban-drop-zone' + (over ? ' over' : '')}
            style={{ borderColor: color, color: color, background: over ? color + '1A' : undefined }}
            onDragOver={function (e) {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                setOver(true);
            }}
            onDragLeave={function () { setOver(false); }}
            onDrop={function (e) {
                e.preventDefault();
                setOver(false);
                if (draggingCard && onDropped) onDropped(draggingCard);
            }}
        >
            {zone.icon && <i className={'dx-icon-' + zone.icon} />}
            <span>{label}</span>
        </div>
    );
}

// ─── Main Component ──────────────────────────────────────────────
export default forwardRef(function AntKanbanBoard(props, ref) {
    var {
        // Data source
        API,
        session,
        searchConditions,
        items: itemsProp,

        // Column config
        columns: columnsProp,
        statusField = 'task_status',

        // Card config
        cardFields,

        // Event handlers
        onDragEnd: onDragEndProp,
        onCardClick,
        onCardDoubleClick,

        // UI options
        height,
        showColumnCount = true,
        showSearch = false,
        refreshInterval,
        compact = false,

        // Tổng theo cột: { field, format: 'money' } — header hiện Σ field các card trong cột
        columnSummary,
        // Vùng thả hành động khi đang kéo card: [{ key, label, color, icon? }] + onDropAction(key, card).
        // Thả vào vùng KHÔNG tự update — caller toàn quyền (popup xác nhận...).
        dropActions,
        onDropAction,
        // Slot toolbar tùy biến (render giữa search và board)
        renderToolbar,
        // Menu ⋮ trên card: { items: [{key, label, icon?, visible?(card)}], onSelect(key, card) }
        cardMenu,

        // Mock mode
        mockMode = false,

        // Visibility condition
        visible
    } = props;

    var [cards, setCards] = useState([]);
    var [loading, setLoading] = useState(false);
    var [searchText, setSearchText] = useState('');
    var [draggingCard, setDraggingCard] = useState(null);
    var refreshTimer = useRef(null);

    var columns = columnsProp || MOCK_COLUMNS;

    // ─── Check visibility via AntCondition ──────────────────────
    if (visible && session) {
        var isVis = AntCondition.checkAll({}, visible, session);
        if (!isVis) return null;
    }

    // ─── Load data ──────────────────────────────────────────────
    var loadData = useCallback(async function () {
        if (mockMode) {
            setCards(MOCK_CARDS);
            return;
        }
        if (itemsProp) {
            setCards(itemsProp);
            return;
        }
        if (!API) return;

        setLoading(true);
        try {
            var params = { pageSize: 500, ...(searchConditions || {}) };
            var result = await API.getList(params);
            var rows = (result && result.rows) || (result && result.data && result.data.rows) || [];
            setCards(rows);
        } catch (e) {
            // silent
        }
        setLoading(false);
    }, [API, searchConditions, itemsProp, mockMode]);

    useEffect(function () {
        loadData();
    }, [loadData]);

    // ─── Auto-refresh ───────────────────────────────────────────
    useEffect(function () {
        if (refreshInterval && refreshInterval > 0 && !mockMode) {
            refreshTimer.current = setInterval(loadData, refreshInterval * 1000);
            return function () { clearInterval(refreshTimer.current); };
        }
    }, [refreshInterval, loadData, mockMode]);

    // ─── Expose ref methods ─────────────────────────────────────
    useImperativeHandle(ref, function () {
        return {
            reload: loadData,
            getCards: function () { return cards; }
        };
    });

    // ─── Search filter ──────────────────────────────────────────
    var filteredCards = useMemo(function () {
        if (!searchText) return cards;
        var lower = searchText.toLowerCase();
        return cards.filter(function (card) {
            var titleField = (cardFields && cardFields.title) || 'name';
            var subtitleField = (cardFields && cardFields.subtitle) || 'assignee_name';
            var title = (card[titleField] || '').toLowerCase();
            var subtitle = (card[subtitleField] || '').toLowerCase();
            var code = (card.code || '').toLowerCase();
            return title.includes(lower) || subtitle.includes(lower) || code.includes(lower);
        });
    }, [cards, searchText, cardFields]);

    // ─── Group cards by column ──────────────────────────────────
    var cardsByColumn = useMemo(function () {
        var map = {};
        columns.forEach(function (col) { map[col.value] = []; });
        filteredCards.forEach(function (card) {
            var colVal = card[statusField];
            if (map[colVal]) {
                map[colVal].push(card);
            }
        });
        return map;
    }, [filteredCards, columns, statusField]);

    // ─── Drag & Drop ────────────────────────────────────────────
    var handleDrop = useCallback(function (cardId, toColumn) {
        var card = cards.find(function (c) { return (c.id || c.code) === cardId; });
        if (!card) return;

        var fromColumn = card[statusField];
        if (fromColumn === toColumn) return;

        // WIP limit check
        var targetCol = columns.find(function (c) { return c.value === toColumn; });
        if (targetCol && targetCol.wipLimit > 0) {
            var currentCount = (cardsByColumn[toColumn] || []).length;
            if (currentCount >= targetCol.wipLimit) {
                return; // WIP exceeded
            }
        }

        // Optimistic update
        setCards(function (prev) {
            return prev.map(function (c) {
                if ((c.id || c.code) === cardId) {
                    var updated = { ...c };
                    updated[statusField] = toColumn;
                    return updated;
                }
                return c;
            });
        });

        // Caller-provided handler takes full control of persistence (may run custom actions,
        // confirmations, validation...). Only fall back to the generic status update when the
        // caller does not supply one — running both would double-update / race against it.
        if (onDragEndProp) {
            onDragEndProp(cardId, fromColumn, toColumn, card);
        } else if (API && !mockMode && card.id) {
            var newData = {};
            newData[statusField] = toColumn;
            API.postUpdate(card, newData).catch(function () {
                // Revert on error
                loadData();
            });
        }

        setDraggingCard(null);
    }, [cards, statusField, columns, cardsByColumn, onDragEndProp, API, mockMode, loadData]);

    // ─── Render ─────────────────────────────────────────────────
    return (
        <div
            className={'ant-kanban-board' + (compact ? ' compact' : '')}
            style={height ? { height: height } : undefined}
        >
            {/* Search bar */}
            {showSearch && (
                <div className="kanban-toolbar">
                    <input
                        className="kanban-search"
                        type="text"
                        value={searchText}
                        onChange={function (e) { setSearchText(e.target.value); }}
                        placeholder={t({ vi: 'Tìm kiếm...', en: 'Search...', fr: 'Rechercher...', es: 'Buscar...', id: 'Cari...', ja: '検索...', zh: '搜索...', ko: '검색...', hi: 'खोजें...', ar: 'بحث...' })}
                    />
                    {loading && <span className="kanban-loading">{t({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読み込み中...', zh: '加载中...', ko: '로딩 중...', hi: 'लोड हो रहा...', ar: 'تحميل...' })}</span>}
                </div>
            )}

            {/* Toolbar tùy biến của màn (pipeline selector, filter...) */}
            {renderToolbar ? <div className="kanban-custom-toolbar">{renderToolbar()}</div> : null}

            {/* Board */}
            <div className="kanban-columns">
                {columns.map(function (col) {
                    // Check column visibility via AntCondition
                    if (col.visible && session) {
                        var colVis = AntCondition.checkAll({}, col.visible, session);
                        if (!colVis) return null;
                    }

                    return (
                        <KanbanColumn
                            key={col.value}
                            column={col}
                            cards={cardsByColumn[col.value] || []}
                            cardFields={cardFields || {
                                title: 'name',
                                subtitle: 'assignee_name',
                                badges: [
                                    { field: 'priority', colors: DEFAULT_PRIORITY_COLORS },
                                    { field: 'due_date', type: 'date' }
                                ],
                                footer: [
                                    { field: 'comment_count', icon: 'comment' }
                                ]
                            }}
                            statusField={statusField}
                            showColumnCount={showColumnCount}
                            columnSummary={columnSummary}
                            onCardClick={onCardClick}
                            onDragStart={function (card) { setDraggingCard(card); }}
                            onDragEnd={function () { setDraggingCard(null); }}
                            onDrop={handleDrop}
                            draggingCard={draggingCard}
                            session={session}
                            cardMenu={cardMenu}
                        />
                    );
                })}
            </div>

            {/* Vùng thả hành động — chỉ hiện khi đang kéo card */}
            {dropActions instanceof Array && dropActions.length > 0 && draggingCard && (
                <div className="kanban-drop-zones">
                    {dropActions.map(function (za) {
                        return (
                            <DropZone
                                key={za.key}
                                zone={za}
                                onDropped={function (card) {
                                    setDraggingCard(null);
                                    if (onDropAction) onDropAction(za.key, card);
                                }}
                                draggingCard={draggingCard}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
});
